/**
 * F8 — Quiz finishing-state timing logic (Sprint 14, P0).
 *
 * The quiz enforces a 60s server-side minimum response time
 * (MIN_DURATION_S in /api/quiz/finish, mirrored as MIN_DURATION_MS in the
 * player). Until F8 the client showed a single static "Reading your
 * answers…" line for that whole window, which read as a stuck/frozen page —
 * the user-reported symptom (2026-06-13): "az utolsó kérdés után nem lép
 * tovább.. Az is fura."
 *
 * We make the wait informative: a rotating caption and a progress bar that
 * fills 0→95% linearly across the window, with the final 5% reserved for the
 * actual server response.
 *
 * Sprint 15 hotfix (2026-06-14): the MIN_DURATION was lowered 60s → 6s (the
 * 60s buffer was the dominant cause of the "NAGYON sokáig tart" complaint).
 * CAPTION_ROTATE_MS dropped 5s → 2s in lockstep so the user still sees a few
 * caption transitions (captions 0→1→2) inside the now-6s window rather than
 * a single static line. The pure helpers below take durationMs as a parameter
 * and stay correct at any duration; only the cadence constant changed.
 *
 * This module owns the pure timing math so it can be unit-tested without a
 * DOM (the repo's vitest runs in the node environment — see
 * question-option.test.tsx for the precedent). The component in
 * quiz-player.tsx drives a setInterval and reads these helpers.
 */

/** Rotation cadence — caption advances once per this many ms. */
export const CAPTION_ROTATE_MS = 2_000;

/**
 * Progress fills up to this percentage across the client wait; the last
 * slice (95→100) is triggered by the server response landing, not by time.
 */
export const PROGRESS_CEILING_PCT = 95;

/**
 * The full caption rotation. The last line is held until the server
 * response arrives (captionIndexForElapsed clamps to the final entry), so
 * a slow server never runs the user off the end of the list.
 */
export const FINISHING_CAPTIONS: readonly string[] = [
  "Reading your answers…",
  "Cross-checking with 50,000+ similar profiles…",
  "Identifying your top 3 spending archetypes…",
  "Calculating your hidden leak categories…",
  "Mapping your patterns to behavior signals…",
  "Building your personalized audit…",
  "Almost there — preparing your snapshot…",
  "Finalizing your blind spot analysis…",
  "Ranking your top leaks by impact…",
  "Polishing your one-next-step recommendation…",
  "Loading your result page…",
  "Just a moment more…",
];

/**
 * Which caption index to show after `elapsedMs` of waiting. Advances one
 * step per CAPTION_ROTATE_MS and clamps to the last caption so the line
 * holds steady if the server takes longer than the full rotation.
 */
export function captionIndexForElapsed(elapsedMs: number): number {
  if (!Number.isFinite(elapsedMs) || elapsedMs <= 0) return 0;
  const step = Math.floor(elapsedMs / CAPTION_ROTATE_MS);
  return Math.min(step, FINISHING_CAPTIONS.length - 1);
}

/** Convenience: the caption string for a given elapsed time. */
export function captionForElapsed(elapsedMs: number): string {
  return FINISHING_CAPTIONS[captionIndexForElapsed(elapsedMs)] ?? FINISHING_CAPTIONS[0]!;
}

/**
 * Progress percentage (0–PROGRESS_CEILING_PCT) for `elapsedMs` against the
 * total `durationMs` window. Linear, clamped at both ends. The component
 * jumps straight to 100 separately when the server response resolves.
 */
export function progressForElapsed(elapsedMs: number, durationMs: number): number {
  if (durationMs <= 0) return PROGRESS_CEILING_PCT;
  if (!Number.isFinite(elapsedMs) || elapsedMs <= 0) return 0;
  const ratio = elapsedMs / durationMs;
  const pct = ratio * PROGRESS_CEILING_PCT;
  return Math.max(0, Math.min(PROGRESS_CEILING_PCT, Math.round(pct)));
}
